/**
 * Monthly Charity Component
 *
 * Strict TypeScript conversion for the monthly charity card interactions.
 */

type MonthlyCharityContainer = HTMLElement & {
  dataset: DOMStringMap & {
    pifsterMonthlyInit?: string;
    instanceId?: string;
  };
};

type MonthlyCharityPlayButton = HTMLElement & {
  dataset: DOMStringMap & {
    video?: string;
    title?: string;
  };
};

function syncDescriptionScrollState(content: HTMLElement): void {
  const hasOverflow = content.scrollHeight > content.clientHeight + 1;
  const isAtEnd = !hasOverflow || content.scrollTop + content.clientHeight >= content.scrollHeight - 2;

  content.classList.toggle('is-scrollable', hasOverflow);
  content.classList.toggle('is-scrolled-end', isAtEnd);
}

function extractYouTubeId(url: string): string | null {
  const normalized = url.trim();
  if (normalized === '') {
    return null;
  }

  const patterns = [
    /youtu\.be\/([^#\&\?\/]+)/i,
    /youtube\.com\/watch\?(?:[^&]+&)*v=([^&]+)/i,
    /youtube\.com\/embed\/([^#\&\?\/]+)/i,
    /youtube\.com\/v\/([^#\&\?\/]+)/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  if (/^[a-zA-Z0-9_-]{11}$/.test(normalized)) {
    return normalized;
  }

  return null;
}

function closeYouTubeModal(modal: HTMLElement): void {
  const iframeContainer = modal.querySelector<HTMLElement>('.pifster-youtube-iframe-container');
  if (iframeContainer) {
    iframeContainer.innerHTML = '';
  }

  const titleElem = modal.querySelector<HTMLElement>('#pifster-youtube-modal-title');
  if (titleElem) {
    titleElem.textContent = '';
  }

  modal.classList.remove('open');
  document.body.style.overflow = '';
}

function openYouTubeModal(title: string, videoUrl: string): void {
  const modal = document.getElementById('pifster-youtube-modal');
  if (!modal) {
    throw new Error('YouTube modal element not found. Ensure pifster-youtube-modal is rendered in the footer.');
  }

  const iframeContainer = modal.querySelector<HTMLElement>('.pifster-youtube-iframe-container');
  const titleElem = modal.querySelector<HTMLElement>('#pifster-youtube-modal-title');
  if (!iframeContainer) {
    throw new Error('YouTube iframe container not found in modal.');
  }

  const videoId = extractYouTubeId(videoUrl);
  if (!videoId) {
    throw new Error(`Could not extract a YouTube video ID from URL: ${videoUrl}`);
  }

  iframeContainer.innerHTML = '';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  iframe.allow = 'autoplay; fullscreen';
  iframe.allowFullscreen = true;
  iframe.width = '100%';
  iframe.height = '100%';
  iframe.className = 'pifster-youtube-iframe';
  iframe.title = title;
  iframeContainer.appendChild(iframe);

  if (titleElem) {
    titleElem.textContent = title;
  }

  modal.classList.add('open');
  document.body.style.overflow = 'hidden';

  const closeButton = modal.querySelector<HTMLElement>('#pifster-youtube-modal-close');
  closeButton?.focus();
}

function initInstanceEventHandlers(container: MonthlyCharityContainer, instanceId: string): void {
  if (container.dataset['pifsterMonthlyInit'] === '1') {
    return;
  }

  container.dataset['pifsterMonthlyInit'] = '1';

  const descriptionContent = container.querySelector<HTMLElement>('.monthly-charity-description-content');
  descriptionContent?.addEventListener('scroll', () => {
    syncDescriptionScrollState(descriptionContent);
  }, { passive: true });

  container.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const playButton = target.closest('.monthly-charity-play-launch');
    if (playButton instanceof HTMLElement && 'video' in playButton.dataset && playButton.dataset['video']) {
      event.preventDefault();
      const title = playButton.dataset['title'] || 'Video';
      openYouTubeModal(title, playButton.dataset['video']);
    }
  });

  container.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const toggle = target.closest('.monthly-charity-description-toggle');
    if (!(toggle instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', (!expanded).toString());

    const content = toggle.nextElementSibling;
    const arrow = toggle.querySelector<HTMLElement>('.monthly-charity-turn-arrow');

    if (content instanceof HTMLElement) {
      const isOpening = !expanded;
      content.classList.toggle('open', isOpening);

      if (isOpening) {
        content.setAttribute('tabindex', '0');
        content.scrollTop = 0;
        window.requestAnimationFrame(() => {
          syncDescriptionScrollState(content);
        });
      } else {
        content.removeAttribute('tabindex');
        content.classList.remove('is-scrollable', 'is-scrolled-end');
      }
    }

    if (arrow) {
      arrow.style.transform = expanded ? '' : 'rotate(90deg)';
    }
  });
}

function initMonthlyCharityComponents(): void {
  document.querySelectorAll<HTMLElement>('.monthly-charity-card[data-instance-id]').forEach((card) => {
    const container = card as MonthlyCharityContainer;
    const instanceId = String(container.dataset['instanceId'] ?? '').trim();
    if (!instanceId) {
      return;
    }

    initInstanceEventHandlers(container, instanceId);
  });
}

function bindGlobalListeners(): void {
  document.body.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const closeButton = target.closest('#pifster-youtube-modal-close');
    const overlay = target.closest('#pifster-youtube-modal-overlay');
    if (!closeButton && !overlay) {
      return;
    }

    const modal = document.getElementById('pifster-youtube-modal');
    if (modal) {
      closeYouTubeModal(modal);
    }
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      return;
    }

    const modal = document.getElementById('pifster-youtube-modal');
    if (modal && modal.classList.contains('open')) {
      closeYouTubeModal(modal);
    }
  });
}

function boot(): void {
  bindGlobalListeners();
  initMonthlyCharityComponents();

  const wpGlobal = (window as Window & { wp?: { domReady?: (fn: () => void) => void } }).wp;
  if (wpGlobal && typeof wpGlobal.domReady === 'function') {
    wpGlobal.domReady(initMonthlyCharityComponents);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export {};
